import { Message, type CommandService } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { EditionType } from '@/features/plg-plan/types';
import { getIsOversea } from '@telecom/shared/utils/environment';
import type { PlgAgentFeatureCheckApi } from '@telecom/shared/domain/permission/apis/plg-agent-feature-check-api';
import type { UserInfoService } from '@telecom/shared/domain/permission/services/user-info-service';
import { UserLevel } from '@telecom/shared/domain/permission/services/user-info-service';
import { I18nService } from '@telecom/shared/services/i18n';

const guestForbiddenLocaleLoaders = {
    cn: () => import('../locales/cn.json'),
    en: () => import('../locales/en.json'),
    jp: () => import('../locales/jp.json'),
    tw: () => import('../locales/tw.json'),
};

type GuestForbiddenLocale = keyof typeof guestForbiddenLocaleLoaders;

/**
 * PLG 权限检查选项
 */
export interface CheckPLgPermissionOptions {
    /** 当前任务ID */
    taskId?: string;
    /** 触发来源 */
    trigger_source?: string;
    /** 是否禁止海外游客使用当前功能 */
    guestForbidden?: boolean;
}

/**
 * PLG 权限服务
 *
 * 用于判断当前用户是否有权限使用 PLG 功能，并在无权限时打开升级套餐对话框。
 */
export interface PlgPermissionService {
    /**
     * 检查 PLG 权限
     * 如果有权限则调用回调函数，如果没有权限则打开升级套餐对话框并抛出错误。
     *
     * @param callback - 有权限时执行的回调函数
     * @param options - PLG 权限检查选项
     * @returns Promise<void>
     */
    checkPLgPermission(callback?: () => void | Promise<void>, options?: CheckPLgPermissionOptions): Promise<void>;
}

/**
 * PLG 权限服务使用的 Agent 标识
 */
export type PlgAgentName = 'novelty' | 'fto' | 'design_fto';

/**
 * PLG 权限服务基类
 *
 * 统一处理不同业务模块的 PLG 权限判断流程，子类只需提供 Agent 标识。
 */
@injectable()
export abstract class BasePlgPermissionService implements PlgPermissionService {
    protected abstract readonly _agentName: PlgAgentName;

    @inject(I18nService)
    private readonly _i18nService!: I18nService;

    @inject(Message)
    private readonly _message!: Message;

    private readonly _loadedGuestForbiddenLocales = new Set<GuestForbiddenLocale>();

    constructor(
        private readonly _userInfoService: UserInfoService,
        private readonly _commandService: CommandService,
        private readonly _plgAgentFeatureCheckApi: PlgAgentFeatureCheckApi,
    ) {}

    async checkPLgPermission(callback?: () => void | Promise<void>, options?: CheckPLgPermissionOptions): Promise<void> {
        if (!getIsOversea()) {
            await this._runCallback(callback);
            return;
        }

        const userLevel = await this._userInfoService.userLevel();
        if (options?.guestForbidden && userLevel === UserLevel.Guest) {
            await this._showGuestForbiddenMessage();
            this._openLoginDialog(options);
            throw new Error('No permission to use advanced feature. PLG free quota exceeded.');
        }

        if (userLevel !== UserLevel.Guest && userLevel !== UserLevel.Free) {
            await this._runCallback(callback);
            return;
        }

        if (!options?.taskId) {
            await this._runCallback(callback);
            return;
        }

        const { can_use } = await this._plgAgentFeatureCheckApi.checkPlgAgentFeature({
            task_id: options.taskId,
        });
        if (can_use) {
            await this._runCallback(callback);
            return;
        }

        this._openUpgradeDialog(options);
        throw new Error('No permission to use advanced feature. PLG free quota exceeded.');
    }

    private _openLoginDialog(options?: CheckPLgPermissionOptions): void {
        this._commandService.executeCommand('login:open-dialog', {
            trigger_source: options?.trigger_source,
        });
    }

    private async _showGuestForbiddenMessage(): Promise<void> {
        const locale = this._getGuestForbiddenLocale();
        if (!this._loadedGuestForbiddenLocales.has(locale)) {
            const { default: messages } = await guestForbiddenLocaleLoaders[locale]();
            this._i18nService.mergeLocaleMessage(locale, messages);
            this._loadedGuestForbiddenLocales.add(locale);
        }

        this._message.warning({
            message: this._i18nService.t('plgPermission.guestForbidden'),
        });
    }

    private _getGuestForbiddenLocale(): GuestForbiddenLocale {
        const locale = this._i18nService.locale.value;
        return locale in guestForbiddenLocaleLoaders ? locale as GuestForbiddenLocale : 'en';
    }

    private async _runCallback(callback?: () => void | Promise<void>): Promise<void> {
        if (callback) {
            await callback();
        }
    }

    private _openUpgradeDialog(options?: CheckPLgPermissionOptions): void {
        this._commandService.executeCommand('usage-limit:open-modal', {
            recommendedPlanId: EditionType.ENTERPRISE,
            agent_name: this._agentName,
            trigger_source: options?.trigger_source,
            titleType: 'enterprise_only',
            header_type: 'feature_upgrade_required',
        });
    }
}
